import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import { DispatchType } from "../../shared/utilities";
import { IElementVariable, IVariableScopeDictionary, populateAccessibleReturnsAndVariables } from "../actions/VariableScopeAnalyzer";
import { State } from "../model/State";
import { ValidationError } from "../model/ValidationData";

import { ElementType, IConditionBranchElement, IElement, IProcessVariable, ReturnType, TypesWithName } from "../model/IProcessData";

import { ChangeEvent } from "../../shared/model/Event";
import { addArrayElement } from "../actions/AddArrayElement";
import { removeArrayElement } from "../actions/RemoveArrayElement";
import { changeFromArrayToExpressionType } from "../actions/UpdateParameter";
import { updateSelectedReturnType } from "../actions/UpdateSelectedReturnType";
import { updateAssignVariableSetting, updateBypassGlobalExceptionHandler, updateSelectedTriggerType } from "../actions/UpdateSelectedType";

import { updateElementName } from "../actions/AddOrUpdateElement";
import { reorderArrayElement } from "../actions/ReorderArrayElement";
import { ParameterEditorValue } from "../model/ParameterEditorValue";
import { getProcessReferences, getTriggerReferences } from "../model/References";

import { default as CallActivityEditor } from "./CallActivity/CallActivityEditor";
import { CatchEditor } from "./CatchEditor";
import { ExlusiveConditionBranchEditor } from "./ExclusiveConditionBranchEditor";
import { ExclusiveDecisionEditor } from "./ExclusiveDecisionEditor";
import { ExpressionEditor } from "./ExpressionEditor";
import { ForeachEditor } from "./ForeachEditor";
import { InclusiveConditionBranchEditor } from "./InclusiveConditionBranchEditor";
import { default as LogResultEditor } from "./LogResultEditor";
import { ParametersErrorList } from "./ParametersErrorsList";
import { ReturnEditor } from "./ReturnEditor";
import { default as TaskEditor } from "./TaskEditor";
import { default as TriggerEditor } from "./TriggerEditor";
import { WhileEditor } from "./WhileEditor";

import { loader } from "@monaco-editor/react";
import { languageProvider } from "../../shared/monaco/lang/languageProvider"



// DO not move this from here
// -----------------------------
import { SharedStateEditor } from "./SharedStateEditor";
import { ICompletionTypes } from "../../shared/monaco/model/model";
import { IDisposable } from "monaco-editor";
import { UserSnippet } from "../actions/SnippetActions";
import { elementNameKey } from "processEditor/actions/ValidateProcess";
import { DmnEditor } from "./DmnEditor";

// -----------------------------

export class ParameterEditor extends React.Component<ParameterEditorProps, ParameterEditorOwnState> {

    _isMounted = false;
    disposableLanguageProvider: IDisposable | null = null;

    constructor(props: ParameterEditorProps) {
        super(props);
        this.state = {
            language: ""
        }
    }

    public componentDidMount() {
        this._isMounted = true;
        if (!this.props.isEditDisabled) {
            this.updateCompletionData(this.props.id);
        }
    }

    public disposeLanguageProvider(): void {
        if (this.disposableLanguageProvider !== null) {
            this.disposableLanguageProvider.dispose();
            this.disposableLanguageProvider = null;
        }
    }

    public componentWillUnmount(): void {
        this.disposeLanguageProvider();
        this._isMounted = false;
    }

    public componentDidUpdate(prevProps: ParameterEditorProps, prevState: ParameterEditorOwnState): void {
        if (!this.props.isEditDisabled && this.props.id !== prevProps.id) {
            // selected element has changed -> update completion data
            this.updateCompletionData(this.props.id);
        }
        if (!this.props.isEditDisabled && this.state.language !== prevState.language) {
            this.updateCompletionData(this.props.id);
        }
        if (!this.props.isEditDisabled &&
            (!_.isEqual(this.props.snippets, prevProps.snippets) || !_.isEqual(this.props.environmentVariables, prevProps.environmentVariables))) {
            this.updateCompletionData(this.props.id);
        }
    }

    public shouldComponentUpdate(nextProps: ParameterEditorProps, nextState: ParameterEditorOwnState): boolean {
        if (!_.isEqual(this.props.element, nextProps.element) ||
            !_.isEqual(this.props.validationErrors, nextProps.validationErrors)) {
            return true;
        }
        if (!_.isEqual(this.state.language, nextState.language)) {
            return true;
        }
        // For Monaco suggestions
        if (!_.isEqual(this.props.snippets, nextProps.snippets) ||
            !_.isEqual(this.props.environmentVariables, nextProps.environmentVariables)) {
            return true;
        }
        if (!_.isEqual(this.props.showElementNameInput, nextProps.showElementNameInput)) {
            return true;
        }
        return false;
    }

    public setLang(language: string): void {
        if (this._isMounted) {
            this.setState({ language })
        }
    }

    public render(): JSX.Element | null {
        if (!this.props.element) {
            return null;
        }

        let possibleElementSpecificEditorFields = this.getPossibleElementSpecificEditorFields(this.props.element, this.props.handleTriggerChange, this.props.hasGlobalExceptionHandler);

        // Task parameters can contain multiple navigation tabs which are broken by fieldset disabled on firefox.
        // For tasks, each tab is wrapped in a fieldset in the parameter editor, instead of being fully wrapped here.
        if (this.props.element.type != ElementType.Task) {
            possibleElementSpecificEditorFields = <S.InnerParameterFieldset>{possibleElementSpecificEditorFields}</S.InnerParameterFieldset>
        }

        return (
            <S.ParameterFieldset key={this.props.id + "_parameterEditor"}>
                <ParametersErrorList id={this.props.element.id} validationErrors={this.props.validationErrors} />
                {this.props.showElementNameInput && this.getNameInputEditor()}
                {possibleElementSpecificEditorFields}
            </S.ParameterFieldset>
        );
    }

    private getPossibleElementSpecificEditorFields(elem: IElement, handleTriggerChange: TriggerChangeHandler, hasGlobalExceptionHandler: boolean) {
        switch (elem.type) {
            case ElementType.ConditionBranch: {
                return <ExlusiveConditionBranchEditor id={elem.id} element={elem} disabled={this.props.isEditDisabled} />;
            }
            case ElementType.InclusiveDecisionBranch:
                return <InclusiveConditionBranchEditor id={elem.id} element={elem} disabled={this.props.isEditDisabled} />;
            case ElementType.Task: {
                return <TaskEditor id={elem.id} />;
            }

            case ElementType.Start: {
                return <TriggerEditor id={elem.id} onChange={handleTriggerChange} />;
            }

            case ElementType.GlobalErrorHandler:
            case ElementType.CallActivity:
                return <CallActivityEditor id={elem.id} existingParameters={elem.parameters} />;

            case ElementType.IntermediateReturn:
            case ElementType.Return:
            case ElementType.Throw:
                return <ReturnEditor
                    id={elem.id}
                    element={elem}
                    disabled={this.props.isEditDisabled}
                    hasGlobalExceptionHandler={hasGlobalExceptionHandler}
                    onChangeReturnType={this.props.handleReturnTypeChange}
                    onHttpHeaderArrayItemAdd={this.props.handleHttpHeaderArrayItemAdd}
                    onHttpHeaderArrayItemRemove={this.props.handleHttpHeaderArrayItemRemove}
                    onChangeHttpHeaderArrayToExpression={this.props.handleChangeHttpHeaderArrayToExpression}
                    onHttpHeaderArrayItemIndexChange={this.props.handleHttpHeaderArrayItemIndexChange}
                    onChangeBypassGlobalExceptionHandler={this.props.handleBypassGlobalExceptionHandlerToggleChange}
                />;

            case ElementType.SubProcess:
                return <LogResultEditor elementId={this.props.id} />;
            case ElementType.SequentialForeach:
            case ElementType.ParallelForeach:
                return <ForeachEditor id={elem.id} existingParameters={elem.parameters} shouldDisposeIterator={elem.shouldDispose} />;

            case ElementType.While:
                return <WhileEditor id={elem.id} existingParameters={elem.parameters} />;

            case ElementType.Catch:
                return <CatchEditor id={elem.id} existingParameters={elem.parameters} />;

            case ElementType.Expression:
                return <ExpressionEditor
                    id={elem.id}
                    disabled={this.props.isEditDisabled}
                    element={elem}
                    onChangeAssignVariable={this.props.handleAssignVariableToggleChange}
                />;

            case ElementType.Decision:
                return <ExclusiveDecisionEditor id={elem.id} existingParameters={elem.parameters} />;

            case ElementType.Dmn:
                return <DmnEditor id={elem.id} existingParameters={elem.parameters} />;

            case ElementType.SharedState:
                return <SharedStateEditor id={elem.id} existingParameters={elem.parameters} />;
        }
    }

    private async updateCompletionData(nextId: string) {
        let accessibleVariablesWithinScope = [] as IElementVariable[];
        const accessibleReturnsWithinScope = [] as IElementVariable[];
        if (this.props.element.type !== ElementType.GlobalErrorHandler) {
            populateAccessibleReturnsAndVariables(
                nextId,
                this.props.variableScopeDictionary,
                accessibleReturnsWithinScope,
                accessibleVariablesWithinScope);
        } else {
            accessibleVariablesWithinScope = [{
                elementType: ElementType.GlobalErrorHandler,
                elementId: "globalErrorHandler",
                variableName: "error",
                parameterKey: "error"
            }] as IElementVariable[];
        }

        // Dispose language provider if it already exists, we have to do this because componentWillUnMount will not fire if we change from Task directly to Task.
        // componentWillUnmount will only fire if we click outside of a Tasks' box.
        this.disposeLanguageProvider();

        const completionTypes: ICompletionTypes = {
            environmentVariables: this.props.environmentVariables,
            processReferences: this.props.element.type !== ElementType.Start ? this.props.processReferences : [],
            returnVariables: this.props.element.type !== ElementType.Start ? accessibleReturnsWithinScope : [],
            variables: this.props.element.type !== ElementType.Start ? accessibleVariablesWithinScope : [],
            triggers: this.props.element.type !== ElementType.Start ? this.props.triggerReferences : [],
            snippets: this.props.snippets,
            processVariables: this.props.processVariables.map(pv => pv.name),
            apiSpecPaths: [],
        };

        try {
            const initializedMonaco = await loader.init();
            if (this.disposableLanguageProvider === null) {
                this.disposableLanguageProvider = languageProvider.registerLanguageProvider(initializedMonaco, completionTypes, this.setLang.bind(this));
            }
        } catch (e) {
            console.error(e)
        }

    }

    private getNameInputEditor() {
        if (_.includes(TypesWithName, this.props.element.type)) {
            const hasNameValidationError = !_.isEmpty(this.props.validationErrors) &&
                this.props.validationErrors.some((error) => error.parameterKey === elementNameKey);
            return (
                <div>
                    <S.FormGroup variant={hasNameValidationError ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                        <S.Label>
                            {FRENDS.Localizations.Name}
                        </S.Label>
                        <ParameterEditorElementName
                            key={this.props.element.id}
                            elementName={this.props.elementName}
                            handleLabelChange={this.props.handleLabelChange}
                            disabled={this.props.isEditDisabled}
                        />
                    </S.FormGroup>
                </div>
            );
        }
        return null;
    }
}

// tslint:disable-next-line: max-classes-per-file
export class ParameterEditorElementName extends React.Component<ParameterEditorElementNameProps, ParameterEditorElementNameState> {
    private handleInputThrottled: ((name: string) => void) & _.DebounceSettings;
    constructor(props: ParameterEditorElementNameProps) {
        super(props);
        this.state = {
            elementName: this.props.elementName ?? "",
            editedFromInput: false
        };
        this.handleInputThrottled = _.throttle(props.handleLabelChange, 2000);
    }

    public componentDidUpdate() {
        // this alows editing from the bpmn canvas to update the name input without closing and opening the ParameterEditor
        if (this.props.elementName != this.state.elementName && this.state.editedFromInput === false) {
            if (this.props.elementName != null) {
                this.setState({ elementName: this.props.elementName })
            }
        }
    }

    public render() {
        return (
            <S.Input
                className="parameter-element-name-selector"
                type="text"
                onChange={this.handleChange}
                value={this.state.elementName}
                disabled={this.props.disabled}
                isFullWidth={true}
            />
        );
    }

    private handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ elementName: event.target.value, editedFromInput: true }, () =>
            this.handleInputThrottled(this.state.elementName)
        );
    }
}

interface ParameterEditorElementNameProps {
    elementName: string | null | undefined;
    handleLabelChange: (event: string | null | undefined) => void;
    disabled: boolean | undefined;
}

interface ParameterEditorElementNameState {
    elementName: string;
    editedFromInput: boolean;
}

interface ParameterEditorOwnProps {
    id: string;
}

interface ParameterEditorStateProps {
    element: IElement;
    elementName: string | null | undefined;
    validationErrors: ValidationError[];
    doesDefaultDecisionBranchExist: boolean | undefined;
    environmentVariables: string[];
    processReferences: string[];
    manualTriggerParameters: string[];
    variableScopeDictionary: IVariableScopeDictionary;
    triggerReferences: string[];
    elements: IElement[];
    isEditDisabled: boolean | undefined;
    hasGlobalExceptionHandler: boolean;
    snippets: UserSnippet[];
    processVariables: IProcessVariable[];
    showElementNameInput?: boolean;
}

export interface ParameterEditorDispatchProps {
    handleReturnTypeChange: (newReturnType: ReturnType) => void;
    handleHttpHeaderArrayItemAdd: (key: string, parameters: any) => void;
    handleHttpHeaderArrayItemRemove: (key: string, index: number) => void;
    handleChangeHttpHeaderArrayToExpression: (key: string) => void;
    handleHttpHeaderArrayItemIndexChange: (key: string, oldIndex: number, newIndex: number) => void;
    handleLabelChange: (name: string) => void;
    handleTriggerChange: TriggerChangeHandler;
    handleAssignVariableToggleChange: AssignVariableChangeHandler;
    handleBypassGlobalExceptionHandlerToggleChange: (value: ParameterEditorValue) => void;
}

type TriggerChangeHandler = (triggerId: string) => void;

export type AssignVariableChangeHandler = (toggleValue: ParameterEditorValue, toggleKey: string, variableValue: string, variableKey: string) => void;

export type ParameterEditorProps = ParameterEditorOwnProps & ParameterEditorStateProps & ParameterEditorDispatchProps;

interface ParameterEditorOwnState {
    language: string;
}

const mapParameterEditorStateToProps = (state: State, ownProps: ParameterEditorOwnProps): ParameterEditorStateProps => {
    const environmentVariables = state.environmentVariables;
    const manualTriggerParameters = state.process.manualTrigger.map((x) => x.name);
    const elements = _.map(state.elements, (e) => e);

    const element: IElement = state.elements[ownProps.id];
    const validationElements: ValidationError[] = state.validation.validationDictionary[ownProps.id];
    let defaultBranchExists;
    if (!element) {
        // tslint:disable-next-line:no-console
        console.log("Element not in elements dictionary:" + ownProps.id);
    } else {
        if (element.type === ElementType.ConditionBranch) {
            const decisionElement = element as IConditionBranchElement;
            defaultBranchExists = _.findKey(state.elements, { isDefault: true, decisionElementId: decisionElement.decisionElementId } as never) == null ? false : true;
        }
    }

    const isElementOtherThanTask = state.elements[ownProps.id].type !== ElementType.Task;
    const showElementNameInput = state.uiState.isEditingTask || isElementOtherThanTask;

    return {
        element: element,
        elementName: element != null ? element.name : null,
        doesDefaultDecisionBranchExist: defaultBranchExists,
        validationErrors: validationElements,
        processReferences: getProcessReferences(),
        variableScopeDictionary: state.validation.variableScopeDictionary,
        environmentVariables: environmentVariables,
        elements: elements,
        manualTriggerParameters: manualTriggerParameters,
        triggerReferences: getTriggerReferences(elements.filter((x) => x.type === ElementType.Start), manualTriggerParameters, state.process.isForMonitoringRule),
        isEditDisabled: state.uiState.disableEdit,
        hasGlobalExceptionHandler: state.process.hasGlobalErrorHandler,
        snippets: state.snippets,
        processVariables: state.processVariables,
        showElementNameInput
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>, ownProps: ParameterEditorOwnProps): ParameterEditorDispatchProps => {
    return {
        handleReturnTypeChange: (newReturnType: ReturnType) => dispatch(updateSelectedReturnType(ownProps.id, newReturnType)),
        handleHttpHeaderArrayItemAdd: (key: string, parameters: any) => dispatch(addArrayElement(ownProps.id, key, parameters)),
        handleHttpHeaderArrayItemRemove: (key: string, index: number) => dispatch(removeArrayElement(ownProps.id, key, index)),
        handleChangeHttpHeaderArrayToExpression: (key: string) => dispatch(changeFromArrayToExpressionType(ownProps.id, key)),
        handleTriggerChange: (triggerId) => dispatch(updateSelectedTriggerType(ownProps.id, triggerId)),
        handleLabelChange: (newName) => dispatch(updateElementName(ownProps.id, newName)),
        handleAssignVariableToggleChange: (toggleValue, toggleKey, variableValue, variableKey) =>
            dispatch(updateAssignVariableSetting(ownProps.id, toggleValue, toggleKey, variableValue, variableKey)),
        handleHttpHeaderArrayItemIndexChange: (key: string, oldIndex: number, newIndex: number) =>
            dispatch(reorderArrayElement(ownProps.id, key, oldIndex, newIndex)),
        handleBypassGlobalExceptionHandlerToggleChange: (value: ParameterEditorValue) =>
            dispatch(updateBypassGlobalExceptionHandler(ownProps.id, value))
    };
};

export default connect(mapParameterEditorStateToProps, mapDispatchToProps)(ParameterEditor) as React.ComponentType<ParameterEditorOwnProps>;
